import {
  blokToComponent,
  NavigationService,
  StringService,
  TrackingService,
  useTranslation,
} from '@repo/roche-xx-shared';
import { RocheUIMarketo } from '@rocheglobal/roche-ui';
import React, { useEffect, useRef, useState } from 'react';
import { sleep } from '../../utils/sleep';

type MarketoFormInfo = {
  scriptSourceUrl: string;
  marketoFormId: string;
  marketoUrl: string;
  munchkinId: string;
  formId: number;
} | null;

enum MarketoAnalytic {
  LOAD = 'load',
  LOAD_FAILED = 'load_failed',
  SUBMIT = 'submit',
  VALIDATE = 'validate',
  VALIDATION_FAILED = 'validate_failed',
  SUCCESS = 'success',
  LEAD_TRACKING = 'lead_tracking',
}

function generateMarketoFormInfo(marketoProperty: string, marketoUrl: string, marketoId: number): MarketoFormInfo {
  const scriptSourceUrl = `${marketoUrl}/js/forms2/js/forms2.min.js`;
  const marketoFormId = `mktoForm_${marketoId}`;
  const munchkinId = marketoProperty;
  const formId = marketoId;

  if ([scriptSourceUrl, marketoFormId, munchkinId, formId].some((variable) => typeof variable === 'undefined')) {
    return null;
  }

  return {
    scriptSourceUrl,
    marketoFormId,
    marketoUrl,
    munchkinId,
    formId,
  };
}

// biome-ignore lint: no-explicit-any: better typings are welcome
export const Marketo = ({ blok, slot, getComponent }: any): JSX.Element => {
  const defaultTracking = {
    event: 'form',
    component_name: 'marketo-form',
  };
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState<boolean>(false);
  const [isSubmissionError, setIsSubmissionError] = useState<boolean>(false);
  const [isLoadingError, setIsLoadingError] = useState<boolean>(false);
  const [activeFormId, setActiveFormId] = useState<string | null>(null);

  const munchkinIdRef = useRef(null);
  const formIdRef = useRef(activeFormId);

  useEffect(() => {
    formIdRef.current = activeFormId?.replace('mktoForm_', '');
  }, [activeFormId]);

  const successMessage = useTranslation('form submitted successfully');
  const errorMessage = useTranslation('an error occurred while submitting the form');

  const trackAnalytics = (action: string, extra: Record<string, string> = {}) =>
    TrackingService.push({
      ...defaultTracking,
      // @ts-expect-error custom Marketo fields - Don't nest them inside additionalParameters (yet)
      component_action: action,
      form_id: formIdRef.current,
      munchkin_id: munchkinIdRef.current,
      ...extra,
    });

  const handleSuccessfulFormSubmission = async () => {
    setIsSubmissionSuccessful(true);
    setIsSubmissionError(false);

    const redirectDelay = 1500;
    const isExternalURL = blok?.link?.linktype === 'url';
    const url = blok?.link?.story?.url || blok?.link?.url;

    if (!url || url === '') return;

    if (isExternalURL && !NavigationService.isWhitelistedDomain(url)) return;

    await sleep(redirectDelay);

    window.location.href = isExternalURL ? url : `/${url}`.replace('', '/');
  };

  const handleFormValidation = (form) => {
    if (!form) {
      trackAnalytics(MarketoAnalytic.LOAD_FAILED);
      setIsLoadingError(true);
      return;
    }

    form.onValidate((isValid: boolean) => {
      if (!isValid) {
        trackAnalytics(MarketoAnalytic.VALIDATION_FAILED);
        setIsSubmissionError(true);
      }
    });

    form.onSubmit(() => {
      trackAnalytics(MarketoAnalytic.SUBMIT);
    });

    form.onSuccess(async () => {
      trackAnalytics(MarketoAnalytic.SUCCESS);
      await handleSuccessfulFormSubmission();
      return false;
    });
  };

  const loadMarketoForm = (marketoUrl: string, munchkinId: string, formId: number) => {
    if (!window.MktoForms2) {
      setIsSubmissionError(true);
      return;
    }

    window.MktoForms2.loadForm(marketoUrl, munchkinId, formId, (form) => {
      handleFormValidation(form);
    });
  };

  const createScriptAndAppendToBody = (scriptSourceUrl, marketoUrl: string, munchkinId: string, formId: number) => {
    const script = document.createElement('script');
    script.src = scriptSourceUrl;
    script.async = true;
    script.onload = () => loadMarketoForm(marketoUrl, munchkinId, formId);
    document.body.appendChild(script);
    return script;
  };

  useEffect(() => {
    const { marketo_property: marketoProperty, marketo_url: marketoUrlFromBlok, form_id: blokFormId } = blok;
    const marketoFormInfo = generateMarketoFormInfo(marketoProperty, marketoUrlFromBlok, blokFormId);
    if (!marketoFormInfo) {
      setIsLoadingError(true);
      return;
    }

    const { scriptSourceUrl, marketoFormId, marketoUrl, munchkinId, formId } = marketoFormInfo;
    munchkinIdRef.current = munchkinId;
    setActiveFormId(marketoFormId);

    let script: HTMLScriptElement | null = null;
    if (!window.MktoForms2) {
      script = createScriptAndAppendToBody(scriptSourceUrl, marketoUrl, munchkinId, formId);
    } else {
      loadMarketoForm(marketoUrl, munchkinId, formId);
    }

    return () => {
      if (script) document.body.removeChild(script);
    };
  }, [blok.embedded_snippet]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const origin = params.get('mkt_tok');

    if (!origin) return;

    trackAnalytics(MarketoAnalytic.LEAD_TRACKING, { lead_origin: origin });
  }, [activeFormId]);

  return (
    <RocheUIMarketo>
      <form id={activeFormId || undefined} style={{ display: isSubmissionSuccessful ? 'none' : 'block' }} />

      <br></br>

      {isSubmissionSuccessful &&
        (blok.success?.[0] ? (
          blokToComponent({ blok: blok.success[0], slot, getComponent })
        ) : (
          <div>{StringService.capitalize(successMessage)}</div>
        ))}

      {isSubmissionError &&
        (blok?.error[0] ? (
          blokToComponent({ blok: blok.error[0], slot, getComponent })
        ) : (
          <div>{StringService.capitalize(errorMessage)}</div>
        ))}

      {isLoadingError && (
        <roche-editor-alert
          slot={slot}
          type='error'
          text='The embed snippet could not be parsed. Please review the embed code and try again.'
        />
      )}
    </RocheUIMarketo>
  );
};
